import { type FC, memo, type PropsWithChildren, useLayoutEffect, useState } from 'react';
import styled from 'styled-components';

export interface ButtonGroupProps {
  /**
   * Some button variants (e.g. `secondary`) do not render a border or
   * background color by default, despite having padding. If they are directly
   * below some text or other content, it appears as if they had an extra
   * margin, which can look bad. Setting this prop to `true` will offset these
   * buttons so that their content is aligned with other content.
   *
   * Note that if the `ButtonGroup` is inside of a wrapper with `overflow:
   * hidden` set, the buttons (or their outlines when focused) may get cut off.
   *
   * You should probably set this to `true` for buttons appearing directly
   * underneath other content, but _not_ in separate contexts like toolbars.
   */
  alignContentWithText?: boolean;
}

const ButtonGroupBase = styled.div`
  display: flex;
  gap: 10px;
`;

// Doing it in this imperative way, instead of using CSS, is necessary for the
// effect to be robust enough to handle some edge cases. With CSS, we can only
// easily use `--padding-horizontal` to add `margin-left` and `margin-right` to
// the buttons themselves, not the entire button group. But we may want to style
// `ButtonGroup` to allow it to wrap its children, in which case we need not
// only the first and last elements to be offset, but all elements touching the
// edge of the wrapper.
//
// This logic will work for any combination of buttons (primary, secondary,
// icon-only) on one line, but for the non-standard case of wrapped children,
// the effect won't be correct if some buttons in the middle have different
// padding than the ones at the beggining/end.
const useAlignContentWithText = (ref: HTMLElement | null, alignContentWithText: boolean) =>
  useLayoutEffect(() => {
    if (!ref) return;

    const firstChild = ref.querySelector(
      ':scope > [data-button-alignable-with-text="true"]:first-child',
    );
    const lastChild = ref.querySelector(
      ':scope > [data-button-alignable-with-text="true"]:last-child',
    );

    if (firstChild && alignContentWithText) {
      const firstChildPadding =
        getComputedStyle(firstChild).getPropertyValue('--padding-horizontal');

      ref.style.setProperty('margin-left', `-${firstChildPadding}`);
    } else {
      ref.style.removeProperty('margin-left');
    }

    if (lastChild && alignContentWithText) {
      const lastChildPadding = getComputedStyle(lastChild).getPropertyValue('--padding-horizontal');

      ref.style.setProperty('margin-right', `-${lastChildPadding}`);
    } else {
      ref.style.removeProperty('margin-right');
    }
  });

const ButtonGroup: FC<PropsWithChildren<ButtonGroupProps>> = ({
  alignContentWithText = false,
  ...props
}) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);

  useAlignContentWithText(ref, alignContentWithText);

  return <ButtonGroupBase ref={setRef} {...props} />;
};

// Memoize component to minimize performance impact from
// `useAlignContentWithText`
export default memo(ButtonGroup);
